import * as React from "react";
import { useTranslation } from "react-i18next";
import { useState, useCallback, forwardRef, useMemo } from "react";
import styled from "styled-components";
import { IconButton, SortableList } from "components/common";
import FieldSelect from "views/components/aggregationwizard/FieldSelect";
import TextOverflowEllipsis from "components/common/TextOverflowEllipsis";
import FieldUnit from "views/components/aggregationwizard/units/FieldUnit";
const ListItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 3px;
`;
const EditFieldSelect = styled.div`
  flex: 1;
`;
const FieldTitle = styled(TextOverflowEllipsis)`
  flex: 1;
`;
const Actions = styled.div`
  display: flex;
`;
const ListItem = forwardRef(
  ({
    className,
    dragHandle,
    fieldSelect = FieldSelect,
    item,
    onChange,
    onRemove,
    selectSize,
    selectedFields,
    showUnit,
    allowCreate
  }, ref) => {
    const { t } = useTranslation("widgets");
    const [isEditing, setIsEditing] = useState(false);
    const _onChange = (newFieldName) => {
      onChange(newFieldName);
      setIsEditing(false);
    };
    return /* @__PURE__ */ React.createElement(ListItemContainer, { className, ref }, isEditing && /* @__PURE__ */ React.createElement(
      EditFieldSelect,
      {
        id: "add-field-select",
        as: fieldSelect,
        allowCreate,
        onChange: _onChange,
        onMenuClose: () => setIsEditing(false),
        autoFocus: true,
        openMenuOnFocus: true,
        clearable: false,
        size: selectSize,
        excludedFields: selectedFields.filter((fieldName) => fieldName !== item.id),
        ariaLabel: t("selected_fields.fields"),
        name: "add-field-select",
        value: item.id,
        "aria-label": t("selected_fields.edit_field", { field: item.title })
      }
    ), !isEditing && /* @__PURE__ */ React.createElement(React.Fragment, null, dragHandle, /* @__PURE__ */ React.createElement(FieldTitle, null, item.title), /* @__PURE__ */ React.createElement(Actions, null, showUnit && /* @__PURE__ */ React.createElement(FieldUnit, { field: item.title }), /* @__PURE__ */ React.createElement(
      IconButton,
      {
        name: "edit_square",
        title: t("selected_fields.edit_field", { field: item.title }),
        onClick: () => setIsEditing(true)
      }
    ), /* @__PURE__ */ React.createElement(
      IconButton,
      {
        name: "delete",
        title: t("selected_fields.remove_field", { field: item.title }),
        onClick: onRemove
      }
    ))));
  }
);
const SelectedFieldsList = ({
  selectedFields,
  onChange,
  selectSize = void 0,
  displayOverlayInPortal = false,
  showUnit = false,
  fieldSelect = void 0,
  allowCreate = false
}) => {
  const fieldsForList = useMemo(() => selectedFields == null ? void 0 : selectedFields.map((field) => ({ id: field, title: field })), [selectedFields]);
  const onChangeField = useCallback(
    (fieldIndex, newFieldName) => {
      const newFields = [...selectedFields];
      newFields[fieldIndex] = newFieldName;
      onChange(newFields);
    },
    [onChange, selectedFields]
  );
  const onRemoveField = useCallback(
    (removedFieldName) => {
      const newFields = selectedFields.filter((fieldName) => fieldName !== removedFieldName);
      onChange(newFields);
    },
    [onChange, selectedFields]
  );
  const SortableListItem = useCallback(
    (props) => {
      const { item, index, dragHandle, className, ref } = props;
      return /* @__PURE__ */ React.createElement(
        ListItem,
        {
          onChange: (newFieldName) => onChangeField(index, newFieldName),
          onRemove: () => onRemoveField(item.id),
          selectSize,
          selectedFields: selectedFields != null ? selectedFields : [],
          item,
          fieldSelect,
          dragHandle,
          className,
          ref,
          showUnit,
          allowCreate
        }
      );
    },
    [selectSize, selectedFields, fieldSelect, showUnit, allowCreate, onChangeField, onRemoveField]
  );
  const onSortChange = useCallback(
    (newFieldsList) => {
      onChange(newFieldsList.map(({ id }) => id));
    },
    [onChange]
  );
  if (!(selectedFields == null ? void 0 : selectedFields.length)) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(
    SortableList,
    {
      items: fieldsForList,
      onMoveItem: onSortChange,
      customListItemRender: SortableListItem,
      displayOverlayInPortal
    }
  );
};
export default SelectedFieldsList;
